import React, { useEffect, useState, useRef } from "react";
import Papa from "papaparse";
import * as Yup from "yup";
import { yupResolver } from "@hookform/resolvers/yup";
import { useForm } from "react-hook-form";
import { toast } from "react-toastify";
import {
  postRevision,
  fetchOldValue,
  findDataEntryInfo,
  associateValueToRule,
} from "../../Services/ChangeService";
import { useAuth } from "../../Context/useAuth";
import SubmitButton from "../SubmitButton/SubmitButton";
import { ChangeStateEnum, PhaseDictionary } from "../../Enum";
import { manResponseType } from "../../interfaces";

interface Props {}

type ChangeFormInputs = {
  manifestationNumber: number;
  id_Phase: number;
  identification: string;
  oldValue?: string;
  newValue?: string;
  comment?: string;
};

const validationSchema = Yup.object().shape({
  id_Phase: Yup.number().required("Phase is required"),
  identification: Yup.string()
    .required("Patient ID is required")
    .matches(/^[A-Z]\d{2}P\d{2}$/, "Format must be like C10P04"),
  manifestationNumber: Yup.number().required(
    "Manifestation is required"
  ),
  oldValue: Yup.string(),
  newValue: Yup.string().when("oldValue", {
    is: (oldValue: string) => !!oldValue,
    then: (schema) => schema.required("New Value is required"),
  }),
  comment: Yup.string(),
});

const ChangeForm: React.FC<Props> = () => {
  const [showFields, setShowFields] = useState(false);
  const [oldValue, setOldValue] = useState<string | null>(null);
  const currentUser = useAuth().user;
  const [filteredSuggestions, setFilteredSuggestions] = useState<string[]>([]);
  const [inputValue, setInputValue] = useState<string>("");
  const [manifestNames, setManifestNames] = useState<Record<string, string>>({});
  const [isSearching, setIsSearching] = useState(false);
  const dropdownRef = useRef<HTMLDivElement>(null);

  const [responseDescriptions, setResponseDescriptions] = useState<
    manResponseType[]
  >([]);
  const [idManResponseAvailable, setIdManResponseAvailable] = useState<
    number[]
  >([]);

  // Load response descriptions
  useEffect(() => {
    const fetchResponseDescription = async () => {
      try {
        const response = await fetch("/response_description.csv");
        const csvText = await response.text();
        Papa.parse(csvText, {
          header: true,
          skipEmptyLines: true,
          complete: (result: any) => {
            const parsed: manResponseType[] = result.data
              .filter(
                (row: any) =>
                  row.Id &&
                  row.Description &&
                  row.ResponseType &&
                  row.id &&
                  row.description_es &&
                  row.description_es !== "#ERROR!"
              )
              .map((row: any) => ({
                Id: Number(row.Id),
                Description: row.Description,
                ResponseType: row.ResponseType,
                id: Number(row.id),
                description_es: row.description_es,
              }));

            setResponseDescriptions(parsed);
            setIdManResponseAvailable(
              Array.from(new Set(parsed.map((r) => r.Id)))
            );
          },
        });
      } catch (error) {
        console.error("Error loading response descriptions:", error);
        toast.error("Failed to load response descriptions");
      }
    };

    fetchResponseDescription();
  }, []);

  const [resolvedValue, setResolvedValue] = useState<string>("");
  const [newIdManResponse, setNewIdManResponse] = useState<number>(0);

  const getNewValueOptions = (): manResponseType[] => {
    return responseDescriptions.filter((desc) => desc.Id === newIdManResponse);
  };

  const getOldValueDescription = (): string => {
    if (!oldValue) return "";
    return resolvedValue || oldValue;
  };

  // Load manifest names
  useEffect(() => {
    const fetchManifestNames = async () => {
      try {
        const response = await fetch("/nombre_manifestaciones.csv");
        const csvText = await response.text();

        Papa.parse(csvText, {
          header: false,
          skipEmptyLines: true,
          complete: (result: any) => {
            const names: Record<string, string> = {};
            result.data.forEach((row: string[]) => {
              if (row[0] && row[1]) {
                names[row[1]] = row[0];
              }
            });
            setManifestNames(names);
          },
        });
      } catch (error) {
        console.error("Error loading manifestation names:", error);
        toast.error("Failed to load manifestation names");
      }
    };

    fetchManifestNames();
  }, []);

  // Close dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target as Node)) {
        setFilteredSuggestions([]);
      }
    };

    document.addEventListener("mousedown", handleClickOutside);
    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, []);

  const {
    register,
    handleSubmit,
    setValue,
    reset,
    formState: { errors },
    watch,
  } = useForm<ChangeFormInputs>({
    resolver: yupResolver(validationSchema),
  });

  const handleSearch = async () => {
    const phaseId = watch("id_Phase");
    const identification = watch("identification");
    const manifestationNumber = watch("manifestationNumber");

    // Validate all required fields
    if (!phaseId) {
      toast.error("Please select a Phase.");
      return;
    }

    if (!identification) {
      toast.error("Please enter a Patient ID.");
      return;
    }

    if (!manifestationNumber) {
      toast.error("Please select a Manifestation.");
      return;
    }

    setIsSearching(true);
    setShowFields(false);
    
    // Reset previous values
    setOldValue(null);
    setResolvedValue("");
    setNewIdManResponse(0);

    try {
      // Find data entry info
      const dataEntryInfo = await findDataEntryInfo(
        phaseId,
        identification,
        manifestationNumber
      );

      // The service now throws errors, so if we reach here, data is valid
      if (!dataEntryInfo || !dataEntryInfo.id) {
        toast.error("Invalid response from server.");
        return;
      }

      // Fetch the old value
      const value = await fetchOldValue(dataEntryInfo.id);
      setOldValue(value);
      setValue("oldValue", value);

      // Process manifestation value for dropdown options
      if (manifestationNumber % 10 === 1) {
        // Manifestations ending in 1 don't need value association
        setResolvedValue("");
        setNewIdManResponse(0);
      } else {
        // Try to resolve value description
        try {
          const associateValue = await associateValueToRule(
            value,
            manifestationNumber.toString(),
            [phaseId],
            idManResponseAvailable,
            responseDescriptions
          );
          setResolvedValue(associateValue[0]);
          setNewIdManResponse(associateValue[1]);
        } catch (error) {
          // If association fails, just use raw value
          console.warn("Could not resolve value association:", error);
          setResolvedValue("");
          setNewIdManResponse(0);
        }
      }

      setShowFields(true);
      toast.success("Data loaded successfully");
    } catch (error: any) {
      // Error is already handled in the service with toast
      console.error("Error during search:", error);
      // Reset states on error
      setOldValue(null);
      setResolvedValue("");
      setNewIdManResponse(0);
    } finally {
      setIsSearching(false);
    }
  };

  const [isSubmitting, setIsSubmitting] = React.useState(false);

  const onSubmit = async (data: ChangeFormInputs) => {
    setIsSubmitting(true);
    
    try {
      // Validate required fields
      if (!data.id_Phase || !data.identification || !data.manifestationNumber) {
        toast.error("All required fields must be filled.");
        return;
      }

      if (!data.newValue) {
        toast.error("Please provide a new value.");
        return;
      }

      // Find data entry info again to ensure it exists
      const dataEntryInfo = await findDataEntryInfo(
        data.id_Phase,
        data.identification,
        data.manifestationNumber
      );

      // Validate data entry info
      if (!dataEntryInfo || !dataEntryInfo.id || !dataEntryInfo.id_patient) {
        toast.error("Unable to retrieve data entry information.");
        return;
      }

      const changeData = {
        ...data,
        id_Patient: dataEntryInfo.id_patient,
        variableName: data.manifestationNumber.toString(),
        dataEntryId: dataEntryInfo.id,
        changedBy: currentUser ? currentUser.userName : "System",
        changeDate: new Date().toISOString(),
        changeState: ChangeStateEnum.OnQuery,
        oldValue: data.oldValue!,
        newValue: data.newValue,
      };

      await postRevision(changeData);
      
      // Reset form after successful submission
      reset();
      setShowFields(false);
      setInputValue("");
      setPatientInput("");
      setPhaseValue("");
      setOldValue(null);
      setResolvedValue("");
      setNewIdManResponse(0);
      
      toast.success("Change submitted successfully!");
    } catch (error: any) {
      console.error("Error submitting change:", error);
      // Error is already shown by the service
    } finally {
      setIsSubmitting(false);
    }
  };

  // Handle input and filter suggestions
  const handleInputChange = (value: string) => {
    setInputValue(value);
    if (value.trim().length > 0) {
      const suggestions = Object.keys(manifestNames).filter((name) =>
        name.toLowerCase().includes(value.toLowerCase())
      );
      setFilteredSuggestions(suggestions);
    } else {
      setFilteredSuggestions([]);
    }
  };

  // Handle suggestion selection
  const handleSuggestionSelect = (name: string) => {
    setInputValue(name);
    const id = manifestNames[name];
    setValue("manifestationNumber", Number(id));
    setFilteredSuggestions([]);
  };

  const [patientInput, setPatientInput] = useState<string>("");

  // Transform patient input to uppercase
  const handlePatientInput = (value: string) => {
    const upperValue = value.toUpperCase();
    setPatientInput(upperValue);
    setValue("identification", upperValue);
  };

  const [phaseValue, setPhaseValue] = useState<string>("");

  // Handle phase selection
  const handlePhaseChange = (value: string) => {
    setPhaseValue(value);
    if (value) {
      setValue("id_Phase", Number(value));
    }
  };

  // Get phase options from dictionary
  const getPhaseOptions = () => {
    return Object.entries(PhaseDictionary).map(([key, label]) => ({
      value: key,
      label: `Phase ${label}`,
    }));
  };

  return (
    <form
      className="max-w-4xl mx-auto bg-white shadow-md rounded-lg p-8"
      onSubmit={(e) => {
        e.preventDefault();
        handleSubmit(onSubmit)(e);
      }}
    >
      <h2 className="text-2xl font-bold text-gray-800 mb-6">Add Revision</h2>

      <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mb-6">
        {/* Manifestation Number - Autocomplete */}
        <div className="relative" ref={dropdownRef}>
          <label htmlFor="manifestationSearch" className="block text-sm font-medium text-gray-700 mb-2">
            Manifestation *
          </label>
          <input
            id="manifestationSearch"
            type="text"
            value={inputValue}
            placeholder="Type to search manifestation..."
            onChange={(e) => handleInputChange(e.target.value)}
            className={`w-full border rounded-lg p-2.5 focus:ring-2 focus:ring-blue-500 focus:border-blue-500 ${
              errors.manifestationNumber ? "border-red-500" : "border-gray-300"
            }`}
          />
          {/* Dropdown suggestions */}
          {filteredSuggestions.length > 0 && (
            <ul className="absolute z-10 w-full bg-white border border-gray-300 rounded-lg mt-1 shadow-lg max-h-48 overflow-y-auto">
              {filteredSuggestions.map((suggestion, index) => (
                <li
                  key={index}
                  onClick={() => handleSuggestionSelect(suggestion)}
                  className="p-3 hover:bg-blue-50 cursor-pointer text-sm transition-colors"
                >
                  {suggestion}
                </li>
              ))}
            </ul>
          )}
          {errors.manifestationNumber && (
            <p className="text-red-500 text-xs mt-1">
              {errors.manifestationNumber.message}
            </p>
          )}
        </div>

        {/* Phase ID - Select */}
        <div>
          <label htmlFor="id_Phase" className="block text-sm font-medium text-gray-700 mb-2">
            Phase *
          </label>
          <select
            id="id_Phase"
            value={phaseValue}
            onChange={(e) => handlePhaseChange(e.target.value)}
            className={`w-full border rounded-lg p-2.5 focus:ring-2 focus:ring-blue-500 focus:border-blue-500 ${
              errors.id_Phase ? "border-red-500" : "border-gray-300"
            }`}
          >
            <option value="">Select a phase...</option>
            {getPhaseOptions().map((option) => (
              <option key={option.value} value={option.value}>
                {option.label}
              </option>
            ))}
          </select>
          {errors.id_Phase && (
            <p className="text-red-500 text-xs mt-1">
              {errors.id_Phase.message}
            </p>
          )}
        </div>

        {/* Patient ID */}
        <div className="md:col-span-2">
          <label htmlFor="identification" className="block text-sm font-medium text-gray-700 mb-2">
            Patient ID *
          </label>
          <input
            id="identification"
            type="text"
            value={patientInput}
            placeholder="Enter Patient ID (e.g., C10P04)"
            onChange={(e) => handlePatientInput(e.target.value)}
            className={`w-full border rounded-lg p-2.5 focus:ring-2 focus:ring-blue-500 focus:border-blue-500 ${
              errors.identification ? "border-red-500" : "border-gray-300"
            }`}
          />
          {errors.identification && (
            <p className="text-red-500 text-xs mt-1">
              {errors.identification.message}
            </p>
          )}
        </div>
      </div>

      {/* Search Button */}
      <div className="mb-6">
        <button
          type="button"
          onClick={handleSearch}
          disabled={isSearching}
          className={`w-full py-3 px-4 text-sm font-medium text-white rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-400 focus:ring-offset-2 transition ${
            isSearching
              ? "bg-blue-400 cursor-not-allowed"
              : "bg-blue-600 hover:bg-blue-700"
          }`}
        >
          {isSearching ? (
            <span className="flex items-center justify-center">
              <svg
                className="animate-spin h-4 w-4 mr-2 text-white"
                xmlns="http://www.w3.org/2000/svg"
                fill="none"
                viewBox="0 0 24 24"
              >
                <circle
                  className="opacity-25"
                  cx="12"
                  cy="12"
                  r="10"
                  stroke="currentColor"
                  strokeWidth="4"
                ></circle>
                <path
                  className="opacity-75"
                  fill="currentColor"
                  d="M4 12a8 8 0 018-8v8H4z"
                ></path>
              </svg>
              Searching...
            </span>
          ) : (
            "Search"
          )}
        </button>
      </div>

      {/* Results Section */}
      {showFields && (
        <div className="bg-gray-50 p-6 rounded-lg border border-gray-200 mb-6">
          <h3 className="text-lg font-semibold text-gray-800 mb-4">
            Data Entry Details
          </h3>
          
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mb-6">
            {/* Old Value */}
            <div>
              <label htmlFor="oldValue" className="block text-sm font-medium text-gray-700 mb-2">
                Current Value
              </label>
              <input
                id="oldValue"
                type="text"
                value={`${getOldValueDescription()}${
                  resolvedValue && resolvedValue !== oldValue
                    ? ` (${oldValue})`
                    : ""
                }`}
                readOnly
                className="w-full border border-gray-300 rounded-lg p-2.5 bg-gray-100 text-gray-600"
              />
            </div>

            {/* New Value */}
            <div>
              <label htmlFor="newValue" className="block text-sm font-medium text-gray-700 mb-2">
                New Value *
              </label>
              {getNewValueOptions().length > 0 ? (
                <select
                  id="newValue"
                  {...register("newValue")}
                  className={`w-full border rounded-lg p-2.5 focus:ring-2 focus:ring-blue-500 focus:border-blue-500 ${
                    errors.newValue ? "border-red-500" : "border-gray-300"
                  }`}
                >
                  <option value="">Select new value...</option>
                  {getNewValueOptions().map((opt) => (
                    <option key={opt.id} value={opt.id}>
                      {`${opt.description_es} (${opt.id})`}
                    </option>
                  ))}
                </select>
              ) : (
                <input
                  id="newValue"
                  type="text"
                  {...register("newValue")}
                  className={`w-full border rounded-lg p-2.5 focus:ring-2 focus:ring-blue-500 focus:border-blue-500 ${
                    errors.newValue ? "border-red-500" : "border-gray-300"
                  }`}
                  placeholder="Enter new value"
                />
              )}
              {errors.newValue && (
                <p className="text-red-500 text-xs mt-1">
                  {errors.newValue.message}
                </p>
              )}
            </div>
          </div>

          {/* Comment - Full Width */}
          <div>
            <label htmlFor="comment" className="block text-sm font-medium text-gray-700 mb-2">
              Comment (Optional)
            </label>
            <textarea
              id="comment"
              rows={4}
              className="w-full border border-gray-300 text-gray-900 rounded-lg p-3 focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
              placeholder="Add any additional comments here..."
              {...register("comment")}
            />
          </div>
        </div>
      )}

      {/* Submit Button */}
      {showFields && (
        <div className="flex justify-end gap-4">
          <button
            type="button"
            onClick={() => {
              reset();
              setShowFields(false);
              setInputValue("");
              setPatientInput("");
              setPhaseValue("");
              setOldValue(null);
            }}
            className="px-6 py-2.5 text-sm font-medium text-gray-700 bg-gray-200 rounded-lg hover:bg-gray-300 focus:outline-none focus:ring-2 focus:ring-gray-400 transition"
          >
            Cancel
          </button>
          <SubmitButton isSubmitting={isSubmitting} text="Submit Change" />
        </div>
      )}
    </form>
  );
};

export default ChangeForm;